import React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { BaseView, BaseText } from '@/components/base'
import { AppButton } from '@/components/AppButton'
import { Modal } from '@/components/Modal'
import { WarningEllipses } from '@/assets/icons'
import { useDeleteSavingGoal } from '@/domain/transfer'
import useOffsets from '@/hooks/useOffsets'

export interface DeleteGoalModalProps {
  isVisible: boolean
  onClose: () => void
  goalId: string
  hasSavedAmount?: boolean
  onAfterDelete?: () => void
}

export const DeleteGoalModal: React.FC<DeleteGoalModalProps> = ({
  isVisible,
  onClose,
  goalId,
  hasSavedAmount = false,
  onAfterDelete,
}) => {
  const { t } = useTranslation()
  const offsets = useOffsets()

  const { mutateAsync: deleteSavingGoal, isPending } = useDeleteSavingGoal({
    onError: () => {
      toast.error(t('api:errors.somethingWrong'), {
        icon: '⚠️',
      })
    },
  })

  const title = hasSavedAmount
    ? t('goal.doNotForgetYourMoney')
    : t('goal.deleteGoal')
  const description = hasSavedAmount
    ? t('goal.doNotForgetYourMoneyDescription')
    : t('goal.deleteGoalDescription')

  const handleDeleteGoal = async () => {
    try {
      await deleteSavingGoal({ saving_goal_id: goalId })
      onAfterDelete?.()
      onClose()
    } catch (error) {
      console.error('Error delete goal: ', error)
      // Toast error is already handled in the hook via onError
    }
  }

  return (
    <Modal
      isOpen={isVisible}
      onClose={!isPending ? onClose : () => {}}
      showHandle={true}
    >
      <BaseView
        data-component="delete-goal-modal"
        style={{
          paddingTop: 20,
          paddingBottom: offsets.bottom - 16,
        }}
      >
        {/* Warning Icon */}
        <BaseView center style={{ marginBottom: 32 }}>
          {hasSavedAmount ? (
            <WarningEllipses size={110} color="#5987FF" secondColor="#fff" />
          ) : (
            <WarningEllipses size={110} color="#F32C3E" secondColor="#fff" />
          )}
        </BaseView>

        {/* Title */}
        <BaseView center px={12} style={{ marginBottom: 16 }}>
          <BaseText
            fontSize={28}
            fontFamily="semibold"
            color="label.default"
            style={{
              textAlign: 'center',
              lineHeight: '34px',
            }}
          >
            {title}
          </BaseText>
        </BaseView>

        {/* Description */}
        <BaseView center style={{ marginBottom: 48 }}>
          <BaseText
            fontSize={16}
            fontFamily="medium"
            color="label.description"
            style={{
              textAlign: 'center',
              lineHeight: '22px',
            }}
          >
            {description}
          </BaseText>
        </BaseView>

        {/* Buttons */}
        <BaseView gap={12}>
          <BaseView row gap={12}>
            <AppButton
              type="secondaryGray"
              onPress={onClose}
              disabled={isPending}
              label={
                hasSavedAmount
                  ? t('form:actions.okay')
                  : t('form:actions.cancel')
              }
              isSharedSpace
            />

            {!hasSavedAmount && (
              <AppButton
                type="primaryGradient"
                onPress={handleDeleteGoal}
                disabled={isPending}
                isLoading={isPending}
                label={t('form:actions.delete')}
                isSharedSpace
              />
            )}
          </BaseView>
        </BaseView>
      </BaseView>
    </Modal>
  )
}
